/* WP-10 Me → Sign-in & security (§14.5.5): passkeys, active sessions,
 * sign-in activity. Revoke/register are step-up protected server-side
 * (§10.2 B5/B7, A18/A19) — routed through the shared step-up gate. */
import { useEffect, useState, type FormEvent } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { portalApi } from '../api/portal-api';
import type { AccountPasskey, AccountSession, ActivityResponse } from '../api/types';
import { passkeyRegister, passkeySupported, toPasskeyError } from '../auth/passkey';
import { isStepUpCancelled, useStepUp } from '../auth/step-up-context';
import { Banner, PrimaryButton, SecondaryButton, TextLink } from '../components/primitives';
import { BottomSheet } from '../components/sheet';
import { SubPage } from './me-pages';
import { CheckIcon, CloseIcon, LockIcon, PasskeyIcon, ShieldIcon, WarningIcon } from '../icons';
import { currentPortalLocale } from '../i18n';
import { formatDateTime } from '../utils/dates';
import { errorText } from '../utils/error-text';

export function SecurityRoutes() {
  return (
    <Routes>
      <Route index element={<SecurityPage />} />
      <Route path="passkeys" element={<PasskeysPage />} />
      <Route path="sessions" element={<SessionsPage />} />
      <Route path="activity" element={<ActivityPage />} />
    </Routes>
  );
}

function SecurityPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [passkeys, setPasskeys] = useState<AccountPasskey[] | null>(null);
  const [sessions, setSessions] = useState<AccountSession[] | null>(null);

  useEffect(() => {
    portalApi
      .get<{ items: AccountPasskey[] }>('/account/passkeys')
      .then((r) => setPasskeys(r.items))
      .catch(() => setPasskeys([]));
    portalApi
      .get<{ items: AccountSession[] }>('/account/sessions')
      .then((r) => setSessions(r.items))
      .catch(() => setSessions([]));
  }, []);

  const rows = [
    {
      icon: <PasskeyIcon size={20} />,
      title: t('me.securityPage.passkeys'),
      sub: passkeys ? t('me.securityPage.count', { count: passkeys.length }) : undefined,
      to: '/me/security/passkeys',
      qid: 'portal.security.passkeys',
    },
    {
      icon: <LockIcon size={20} />,
      title: t('me.securityPage.devices'),
      sub: sessions ? t('me.securityPage.count', { count: sessions.length }) : undefined,
      to: '/me/security/sessions',
      qid: 'portal.security.sessions',
    },
    {
      icon: <ShieldIcon size={20} />,
      title: t('me.securityPage.activity'),
      to: '/me/security/activity',
      qid: 'portal.security.activity',
    },
  ];

  return (
    <SubPage title={t('me.securityPage.title')}>
      <div className="hp-rows">
        {rows.map((r) => (
          <button
            key={r.qid}
            type="button"
            className="hp-listrow"
            onClick={() => navigate(r.to)}
            data-quality-id={r.qid}
          >
            {r.icon}
            <span>
              {r.title}
              {r.sub ? <span className="hp-listrow__sub">{r.sub}</span> : null}
            </span>
            <span className="hp-listrow__chev">›</span>
          </button>
        ))}
      </div>
    </SubPage>
  );
}

function PasskeysPage() {
  const { t } = useTranslation();
  const locale = currentPortalLocale();
  const { withStepUp } = useStepUp();
  const [items, setItems] = useState<AccountPasskey[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rename, setRename] = useState<AccountPasskey | null>(null);
  const [removeTarget, setRemoveTarget] = useState<AccountPasskey | null>(null);
  const [label, setLabel] = useState('');

  const load = () =>
    portalApi
      .get<{ items: AccountPasskey[] }>('/account/passkeys')
      .then((r) => setItems(r.items))
      .catch((err) => setError(errorText(err, t)));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      await withStepUp(() => passkeyRegister());
      await load();
    } catch (err) {
      if (!isStepUpCancelled(err)) setError(errorText(toPasskeyError(err), t));
    } finally {
      setBusy(false);
    }
  };

  const saveRename = async (e: FormEvent) => {
    e.preventDefault();
    if (!rename) return;
    setBusy(true);
    try {
      await portalApi.patch(`/account/passkeys/${rename.id}`, {
        deviceLabel: label.trim(),
      });
      setRename(null);
      await load();
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setBusy(true);
    try {
      await withStepUp(() => portalApi.delete(`/account/passkeys/${removeTarget.id}`));
      setRemoveTarget(null);
      await load();
    } catch (err) {
      if (!isStepUpCancelled(err)) setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SubPage title={t('me.securityPage.passkeys')}>
      {passkeySupported() ? (
        <PrimaryButton
          onClick={add}
          loading={busy}
          icon={<PasskeyIcon size={18} />}
          data-quality-id="portal.passkeys.add"
        >
          {t('me.securityPage.passkeyAdd')}
        </PrimaryButton>
      ) : (
        <Banner style="info">{t('me.securityPage.passkeyUnsupported')}</Banner>
      )}

      {error ? (
        <p className="hp-danger-text" role="alert">
          {error}
        </p>
      ) : null}

      <div className="hp-rows hp-section">
        {(items ?? []).map((p) => (
          <div key={p.id} className="hp-listrow" data-quality-id={`portal.passkey.${p.id}`}>
            <PasskeyIcon size={20} />
            <span>
              {p.deviceLabel || t('me.securityPage.unnamedDevice')}
              <span className="hp-listrow__sub">
                {p.lastUsedAt
                  ? t('me.securityPage.passkeyLastUsed', {
                      date: formatDateTime(p.lastUsedAt, locale),
                    })
                  : t('me.securityPage.passkeyNever')}
                {p.backedUp ? ` · ${t('me.securityPage.backedUp')}` : ''}
              </span>
            </span>
            <span className="hp-row">
              <TextLink
                chevron={false}
                onClick={() => {
                  setRename(p);
                  setLabel(p.deviceLabel ?? '');
                }}
              >
                {t('me.securityPage.passkeyRename')}
              </TextLink>
              <TextLink chevron={false} tone="danger" onClick={() => setRemoveTarget(p)}>
                {t('me.securityPage.passkeyDelete')}
              </TextLink>
            </span>
          </div>
        ))}
        {items && items.length === 0 ? (
          <p className="hp-tertiary">{t('me.securityPage.noPasskeys')}</p>
        ) : null}
      </div>

      <BottomSheet
        open={!!rename}
        onClose={() => setRename(null)}
        title={t('me.securityPage.passkeyRenameTitle')}
      >
        <form onSubmit={saveRename} className="hp-stack">
          <label className="hp-field">
            <span className="hp-field__label">{t('me.securityPage.deviceLabel')}</span>
            <input
              className="hp-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={60}
            />
          </label>
          <PrimaryButton type="submit" loading={busy}>
            {t('common.save')}
          </PrimaryButton>
        </form>
      </BottomSheet>

      <BottomSheet
        open={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        title={t('me.securityPage.passkeyDelete')}
      >
        <div className="hp-stack">
          <p>{t('me.securityPage.passkeyRemoveConfirm')}</p>
          <SecondaryButton danger onClick={confirmRemove} loading={busy}>
            {t('me.securityPage.passkeyDelete')}
          </SecondaryButton>
          <SecondaryButton onClick={() => setRemoveTarget(null)}>
            {t('common.back')}
          </SecondaryButton>
        </div>
      </BottomSheet>
    </SubPage>
  );
}

function SessionsPage() {
  const { t } = useTranslation();
  const locale = currentPortalLocale();
  const { withStepUp } = useStepUp();
  const [items, setItems] = useState<AccountSession[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = () =>
    portalApi
      .get<{ items: AccountSession[] }>('/account/sessions')
      .then((r) => setItems(r.items))
      .catch((err) => setError(errorText(err, t)));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const revoke = async (s: AccountSession) => {
    setBusyId(s.id);
    setError(null);
    try {
      await withStepUp(() => portalApi.delete(`/account/sessions/${s.id}`));
      await load();
    } catch (err) {
      if (!isStepUpCancelled(err)) setError(errorText(err, t));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <SubPage title={t('me.securityPage.devices')}>
      {error ? (
        <p className="hp-danger-text" role="alert">
          {error}
        </p>
      ) : null}
      <div className="hp-rows">
        {(items ?? []).map((s) => (
          <div key={s.id} className="hp-listrow" data-quality-id={`portal.session.${s.id}`}>
            <LockIcon size={20} />
            <span>
              {s.deviceHint || t('me.securityPage.unnamedDevice')}
              <span className="hp-listrow__sub">
                {s.current
                  ? t('me.securityPage.deviceCurrent')
                  : t('me.securityPage.passkeyLastUsed', {
                      date: formatDateTime(s.lastSeenAt, locale),
                    })}
                {s.ipMasked ? ` · ${s.ipMasked}` : ''}
              </span>
            </span>
            {!s.current ? (
              <TextLink
                chevron={false}
                onClick={() => revoke(s)}
                aria-label={t('me.securityPage.deviceSignOut')}
              >
                {busyId === s.id ? '…' : t('me.securityPage.deviceSignOut')}
              </TextLink>
            ) : (
              <CheckIcon size={16} />
            )}
          </div>
        ))}
      </div>
    </SubPage>
  );
}

function ActivityPage() {
  const { t } = useTranslation();
  const locale = currentPortalLocale();
  const [items, setItems] = useState<ActivityResponse['items'] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    portalApi
      .get<ActivityResponse>('/account/activity')
      .then((r) => setItems(r.items))
      .catch((err) => setError(errorText(err, t)));
  }, [t]);

  return (
    <SubPage title={t('me.securityPage.activity')}>
      {error ? (
        <p className="hp-danger-text" role="alert">
          {error}
        </p>
      ) : null}
      <div className="hp-rows">
        {(items ?? []).map((a, i) => (
          <div key={`${a.action}-${a.createdAt}-${i}`} className="hp-listrow">
            {a.outcome === 'SUCCESS' ? <CheckIcon size={18} /> : <WarningIcon size={18} />}
            <span>
              {t(`me.securityPage.actions.${a.action}`, { defaultValue: a.action })}
              <span className="hp-listrow__sub">
                {formatDateTime(a.createdAt, locale)}
                {a.deviceHint ? ` · ${a.deviceHint}` : ''}
              </span>
            </span>
            {a.outcome === 'SUCCESS' ? null : <CloseIcon size={14} />}
          </div>
        ))}
        {items && items.length === 0 ? (
          <p className="hp-tertiary">{t('me.securityPage.activityEmpty')}</p>
        ) : null}
      </div>
    </SubPage>
  );
}
